import React, { forwardRef, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { type IconProps } from '@atlaskit/icon/types';
import { ExitingPersistence, SlideIn } from '@atlaskit/motion';
import { Anchor, Box, Flex, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import Suspense from '@atlaskit/react-ufo/suspense';
import { ReactRenderer } from '@atlaskit/renderer';
import { token } from '@atlaskit/tokens';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { skillIconMap } from '@atlassian/rovo-platform-ui-components/skills/skill-icons';
import { SkillHoverCard } from '@atlassian/rovo-platform-ui-components/skills/SkillHoverCard';

import { type ConversationAction, type SkillMetadataMap } from '../../types';
import { isChatAction, isNestedAction as isNestedActionUtil, isSkillAction } from '../../utils';
import { getSkillMetadataForAction } from '../../utils/get-skill-metadata-for-action';

import messages from './messages';

const styles = cssMap({
	buttonContentStyles: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
	},

	secondaryButtonContentStyles: {
		alignItems: 'start',
		gap: token('space.150'),
		display: 'flex',
	},

	inlineItemStyles: {
		color: token('color.text.subtle'),
		gap: token('space.100'),
	},

	iconBoxStyles: {
		width: '32px',
		height: '32px',
		minWidth: '32px',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
	},

	skillActionStackStyles: {
		minHeight: '38px',
		justifyContent: 'center',
	},

	pressableStyles: {
		backgroundColor: token('color.background.neutral.subtle'),
		width: '100%',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		textDecoration: 'none',
		color: token('color.text.subtle'),

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			borderRadius: token('radius.small'),
			textDecoration: 'none',
			color: token('color.text.subtle'),
		},

		'&:active': {
			transform: 'scale(0.98)',
		},
	},

	actionTitleStyles: {
		color: token('color.text.subtle'),
		fontFamily: token('font.family.body'),
		fontStyle: 'normal',
		fontWeight: token('font.weight.medium'),
		textAlign: 'start',
	},

	secondaryActionTitleStyles: {
		color: token('color.text.subtle'),
		font: token('font.body.small'),
		fontFamily: token('font.family.body'),
		fontStyle: 'normal',
		fontWeight: token('font.weight.medium'),
		textAlign: 'start',
	},

	secondaryDescriptionStyles: {
		color: token('color.text.subtle'),
		font: token('font.body.small'),
		fontFamily: token('font.family.body'),
		fontStyle: 'normal',
		fontWeight: token('font.weight.regular'),
		textAlign: 'start',
		paddingTop: token('space.050'),
	},

	secondaryIconStyles: {
		paddingBottom: token('space.050'),
	},

	rollingStripDescriptionExpandable: {
		display: 'grid',
		overflow: 'hidden',
		transition: `grid-template-rows 100ms cubic-bezier(0.2,0,0,1)`, // same duration.small and easeOut as ADS
	},

	rollingStripDescriptionExpandableInner: {
		minHeight: '0px',
		overflow: 'hidden',
	},
});

// Override the paragraph component used by the ReactRenderer to use the subtle text color
const paragraphOverride = ({ children }: { children: React.ReactNode }) => (
	<Text as="p" color="color.text.subtle">
		{children}
	</Text>
);

// Helper component to render title with consistent styling
const TitleRenderer = ({
	title,
	variant = 'default',
}: {
	title: string;
	variant?: 'default' | 'secondary';
}) => {
	const nodeComponents = { paragraph: paragraphOverride };

	return (
		<Box
			xcss={variant === 'secondary' ? styles.secondaryActionTitleStyles : styles.actionTitleStyles}
		>
			<ReactRenderer document={encodeAdf(title)} nodeComponents={nodeComponents} />
		</Box>
	);
};

// Component for rendering action with secondary icon/description
const SecondaryActionContent = ({
	action,
	title,
	isHovered,
	getSecondaryDescription,
}: {
	action?: ConversationAction;
	title: string;
	isHovered?: boolean;
	getSecondaryDescription: (action?: ConversationAction) => string | undefined;
}) => {
	const description = getSecondaryDescription(action);
	const SecondaryIcon = action?.secondaryIcon;
	const alwaysShowSecondaryText = !!action?.alwaysShowSecondaryText;
	const showSecondaryText = alwaysShowSecondaryText || isHovered;

	return (
		<>
			{SecondaryIcon && (
				<Box xcss={styles.secondaryIconStyles}>
					<SecondaryIcon label="" />
				</Box>
			)}
			<TitleRenderer title={title} variant={isHovered ? 'secondary' : 'default'} />
			{description && (
				<Box
					xcss={styles.rollingStripDescriptionExpandable}
					style={{ gridTemplateRows: showSecondaryText ? '1fr' : '0fr' }}
				>
					<Box xcss={styles.rollingStripDescriptionExpandableInner}>
						<Box xcss={styles.secondaryDescriptionStyles}>{description}</Box>
					</Box>
				</Box>
			)}
		</>
	);
};

// Component for rendering skill action content
const SkillActionContent = ({
	action,
	isHovered,
}: {
	action: ConversationAction;
	isHovered?: boolean;
}) => {
	if (!isSkillAction(action) || !action.name) {
		return null;
	}

	const showDescription = (isHovered || action.alwaysShowSecondaryText) && !!action.description;

	return (
		<Stack space="space.025" alignInline="start" xcss={styles.skillActionStackStyles}>
			<Text align="start">{action.name}</Text>
			{showDescription && (
				<Text size="small" color="color.text.subtlest" maxLines={1}>
					{action.description}
				</Text>
			)}
		</Stack>
	);
};

const BaseActionItem = ({
	title,
	icon,
	action,
	isHovered,
	badge,
}: {
	title: string;
	icon: (props: IconProps) => React.JSX.Element;
	action?: ConversationAction;
	isHovered?: boolean;
	badge?: React.ReactNode;
}) => {
	const { formatMessage } = useIntl();
	const Icon = icon;

	const getSecondaryDescription = useCallback(
		(action?: ConversationAction) => {
			if (!action) {
				return undefined;
			}
			if (isChatAction(action) && action.agent) {
				return formatMessage(messages.byAgentText, { agentName: action.agent.name });
			}
			if (action.secondaryDescription) {
				return action.secondaryDescription;
			}
			return undefined;
		},
		[formatMessage],
	);

	return (
		<Box
			xcss={
				getSecondaryDescription(action) || action?.secondaryIcon
					? styles.secondaryButtonContentStyles
					: styles.buttonContentStyles
			}
		>
			<Flex justifyContent="center" alignItems="center" xcss={styles.iconBoxStyles}>
				<Suspense fallback={null} interactionName="action-item-icon">
					<Icon label="" primaryColor={token('color.icon.subtle')} />
				</Suspense>
			</Flex>
			<Inline xcss={styles.inlineItemStyles} alignBlock="center">
				{getSecondaryDescription(action) || action?.secondaryIcon ? (
					<Stack alignInline="start">
						<SecondaryActionContent
							action={action}
							title={title}
							isHovered={isHovered}
							getSecondaryDescription={getSecondaryDescription}
						/>
					</Stack>
				) : action && isSkillAction(action) && action.name ? (
					<SkillActionContent action={action} isHovered={isHovered} />
				) : (
					<TitleRenderer title={title} />
				)}
				{badge}
			</Inline>
		</Box>
	);
};

type DynamicSurfaceActionItemProps = {
	title: string;
	icon: (props: IconProps) => React.JSX.Element;
	testId?: string;
	onClick?: () => void;
	href?: string;
	action?: ConversationAction;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	skillMetadataMap?: SkillMetadataMap;
	badge?: React.ReactNode;
};

export const DynamicSurfaceActionItem: React.ForwardRefExoticComponent<
	DynamicSurfaceActionItemProps & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, DynamicSurfaceActionItemProps>(
	(
		{
			title,
			icon,
			testId,
			onClick,
			href,
			onMouseEnter,
			onMouseLeave,
			action,
			skillMetadataMap,
			badge,
		},
		ref,
	) => {
		const [isHovered, setIsHovered] = useState(false);
		const isNestedAction = action ? isNestedActionUtil(action) : false;

		const onPressableMouseEnter = useCallback(() => {
			setIsHovered(true);
			onMouseEnter?.();
		}, [onMouseEnter]);

		const onPressableMouseLeave = useCallback(() => {
			setIsHovered(false);
			onMouseLeave?.();
		}, [onMouseLeave]);

		const skillMetadataForSlashCommand = getSkillMetadataForAction(action, skillMetadataMap);

		const shouldShowSkillHoverCard =
			action && isSkillAction(action) && skillMetadataForSlashCommand;

		const enhancedAction = shouldShowSkillHoverCard
			? {
					...action,
					name: title,
					description: skillMetadataForSlashCommand.description,
				}
			: action;

		const resolvedIcon =
			shouldShowSkillHoverCard && skillMetadataForSlashCommand.iconName
				? ((skillIconMap[skillMetadataForSlashCommand.iconName] as (
						props: IconProps,
					) => React.JSX.Element) ?? icon)
				: icon;

		const baseActionItem = (
			<BaseActionItem
				title={title}
				icon={resolvedIcon}
				action={enhancedAction}
				isHovered={isHovered}
				badge={badge}
			/>
		);

		const item = shouldShowSkillHoverCard ? (
			<SkillHoverCard
				name={skillMetadataForSlashCommand.name}
				iconName={skillMetadataForSlashCommand?.iconName}
				description={skillMetadataForSlashCommand?.description || ''}
				product={skillMetadataForSlashCommand?.product}
				productIconUrl={skillMetadataForSlashCommand?.productIconUrl}
				shouldRenderToParent
			>
				{baseActionItem}
			</SkillHoverCard>
		) : (
			baseActionItem
		);

		return href ? (
			<Anchor
				href={href}
				target="_blank"
				onClick={onClick}
				xcss={styles.pressableStyles}
				onMouseEnter={onPressableMouseEnter}
				onMouseLeave={onPressableMouseLeave}
			>
				<Inline alignBlock="center" spread="space-between" grow="fill">
					{item}
					<ExitingPersistence appear={isHovered}>
						{isHovered && (
							<SlideIn
								enterFrom="left"
								exitTo="left"
								fade="in"
								animationTimingFunction="ease-out"
								duration="medium"
							>
								{(props) => (
									<div {...props}>
										<LinkExternalIcon label="" />
									</div>
								)}
							</SlideIn>
						)}
					</ExitingPersistence>
				</Inline>
			</Anchor>
		) : (
			<Pressable
				onClick={onClick}
				onMouseEnter={onPressableMouseEnter}
				onMouseLeave={onPressableMouseLeave}
				ref={ref}
				testId={testId}
				xcss={styles.pressableStyles}
			>
				<Inline
					alignBlock="center"
					spread={isNestedAction ? 'space-between' : undefined}
					grow={isNestedAction ? 'fill' : undefined}
				>
					{item}
					<ExitingPersistence appear={isNestedAction && isHovered}>
						{isNestedAction && isHovered && (
							<SlideIn
								enterFrom="left"
								exitTo="left"
								fade="in"
								animationTimingFunction="ease-out"
								duration="medium"
							>
								{(props) => (
									<div {...props}>
										<ArrowRightIcon label="" />
									</div>
								)}
							</SlideIn>
						)}
					</ExitingPersistence>
				</Inline>
			</Pressable>
		);
	},
);
